import React, { useEffect, useState } from 'react'
import { faChevronDown, faChevronUp, faCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { capitalise } from '../../../utilities/constants'
import constants from '../../../utilities/constants'
import { selectSkillName, selectExperienceLevel, selectSubskills, updateSubskillLevel, saveSubskillScores, selectSubskillsSaving, selectTargetRole, setHelpLevel, updateUserHelpLevel, selectIsRoleFlow, selectHelpLevel } from '../../../redux/skillSlice'
import { selectUserName } from '../../../redux/nameSlice'
import { selectInitSessionUserDetails } from '../../../redux/onboardingUserDetails'
import ProgressBar from '../../../components/ProgressBar'
import { toast } from 'react-toastify'
import { selectIsAnalysisPageVisited } from '../../../redux/newAnalysisSlice'
import { Mixpanel } from '../../../config/Mixpanel'

const OnboardingStep3 = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Get data from Redux
  const skillName = useSelector(selectSkillName) || 'Product Management'
  const subSkills = useSelector(selectSubskills)
  // const step1Data = useSelector(selectOnboardingStep1Data) // For future use
  const targetRole = useSelector(selectTargetRole)
  // Get user experience level from previous step
  const experienceLevel = useSelector(selectExperienceLevel)
  console.log('Step 3 - User Experience Level:', experienceLevel)

  // Get help level from store
  const helpLevelFromStore = useSelector(selectHelpLevel)

  // Get user name from Redux
  const userName = useSelector(selectUserName)
  const firstName = userName?.split(" ")?.[0] || 'User'

  // Get session details and saving state
  const userSessionDetails = useSelector(selectInitSessionUserDetails)
  const subskillsSaving = useSelector(selectSubskillsSaving)

  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited);
  const isPricingPageVisited = useSelector(selectIsAnalysisPageVisited);
  const isRoleFlow = useSelector(selectIsRoleFlow);


  const isBeginner = experienceLevel === "beginner"

  // Sample sub-skills for Product Management (you can make this dynamic based on skillName)
  // const subSkills = [
  //   { id: 1, name: 'Product Thinking', level: null },
  //   { id: 2, name: 'User Research', level: null },
  //   { id: 3, name: 'Data Analysis', level: null },
  //   { id: 4, name: 'Market Research', level: null },
  //   { id: 5, name: 'Strategic Planning', level: null }
  // ]

  // Use subSkills directly - no need for separate state
  const skillLevels = subSkills || []
  const [expandedCard, setExpandedCard] = useState(1) // Default to first card expanded
  const [hoveredLevel, setHoveredLevel] = useState({ skillId: null, level: null })

  const [rateSelected, setRateSelected] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);


  // Experience options for beginners
  const experienceOptions = [
    {
      value: 'step_by_step_path',
      text: 'I Need A Step-By Step Path',
      emoji: '📋'
    },
    {
      value: 'dont_know_where_to_start',
      text: 'I Don\'t Know Where To Start',
      emoji: '🤔'
    },
    {
      value: 'practice_with_projects',
      text: 'I Want To Practice With Projects',
      emoji: '💻'
    },
    {
      value: 'basics_with_practice',
      text: 'I Want Basics With Practice',
      emoji: '📚'
    }
  ];

  const handleOptionClick = (option) => {

    try {
      setSelectedLevel(option);
      dispatch(setHelpLevel(option.value))
      dispatch(updateUserHelpLevel({
        session_id: userSessionDetails?.session_id,
        style: option.value
      }))

      // Track beginner option selection
      Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_BEGINNER_OPTION_SELECTED, {
        selected_option: option.value,
        option_text: option.text,
        option_emoji: option.emoji,
        skill_name: skillName || '',
        target_role: targetRole || '',
        user_name: firstName,
        session_id: userSessionDetails?.session_id || '',
        flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
        next_step: '/onboarding/4',
        timestamp: new Date().toISOString()
      })

      navigate('/onboarding/4')
    } catch (error) {
      console.error('Error in handleOptionClick:', error)
    }

  };

  console.log('skillLevels', expandedCard)

  // Update selectedLevel when helpLevelFromStore changes or on initial load
  useEffect(() => {
    if (helpLevelFromStore) {
      const matchingOption = experienceOptions.find(option => option.value === helpLevelFromStore);
      if (matchingOption && selectedLevel?.value !== matchingOption.value) {
        setSelectedLevel(matchingOption);
      }
    }
  }, [helpLevelFromStore, selectedLevel]);


  useEffect(() => {
    if (isAnalysisPageVisited) {
      navigate('/onboarding/analysis')
    }

    else if (isPricingPageVisited) {
      navigate('/onboarding/pricing')
    }

  }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, navigate])

  // Track page load
  useEffect(() => {
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_PAGE_LOADED, {
      flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
      experience_level: experienceLevel,
      is_beginner: isBeginner,
      skill_name: skillName || '',
      target_role: targetRole || '',
      user_name: firstName,
      session_id: userSessionDetails?.session_id || '',
      current_step: isRoleFlow ? 5 : 3,
      total_steps: isRoleFlow ? 8 : 6,
      available_subskills_count: subSkills?.length || 0,
      timestamp: new Date().toISOString()
    })
  }, [isRoleFlow, experienceLevel, isBeginner, skillName, targetRole, firstName, userSessionDetails?.session_id, subSkills?.length])

  const levelOptions = [
    { value: 'new', label: 'New', color: '#70AEFF', bgColor: 'rgba(112, 174, 255, 0.1)' },
    { value: 'basic', label: 'Basic', color: '#F5B631', bgColor: 'rgba(245, 182, 49, 0.1)' },
    { value: 'skilled', label: 'Skilled', color: '#7FC241', bgColor: 'rgba(127, 194, 65, 0.1)' },
    { value: 'expert', label: 'Expert', color: '#406AFF', bgColor: 'rgba(64, 106, 255, 0.1)' }
  ]

  const handleCardClick = (skillId) => {
    const wasExpanded = expandedCard === skillId
    const newExpandedCard = wasExpanded ? null : skillId
    const skillData = skillLevels.find(skill => skill.id === skillId)
    
    setExpandedCard(newExpandedCard)

    // Track card expansion/collapse
    if (wasExpanded) {
      Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_SKILL_CARD_COLLAPSED, {
        skill_id: skillId,
        skill_name: skillData?.name || '',
        skill_current_level: skillData?.level || null,
        skill_name_context: skillName || '',
        session_id: userSessionDetails?.session_id || '',
        timestamp: new Date().toISOString()
      })
    } else {
      Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_SKILL_CARD_EXPANDED, {
        skill_id: skillId,
        skill_name: skillData?.name || '',
        skill_current_level: skillData?.level || null,
        skill_name_context: skillName || '',
        session_id: userSessionDetails?.session_id || '',
        timestamp: new Date().toISOString()
      })
    }
  }

  const handleLevelSelect = (skillId, levelValue) => {
    // Find the current skill index and the next skill BEFORE updating Redux
    const currentIndex = skillLevels.findIndex(skill => skill.id === skillId)
    const nextIndex = currentIndex + 1
    const nextSkill = skillLevels[nextIndex]
    const skillData = skillLevels.find(skill => skill.id === skillId)
    const previousLevel = skillData?.level

    // Update Redux store directly - no local state needed
    dispatch(updateSubskillLevel({ skillId, level: levelValue }))

    // Track skill level rating
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_SKILL_LEVEL_RATED, {
      skill_id: skillId,
      skill_name: skillData?.name || '',
      previous_level: previousLevel,
      new_level: levelValue,
      skill_index: currentIndex + 1,
      total_skills: skillLevels.length,
      will_expand_next: nextSkill?.id <= 5,
      next_skill_name: nextSkill?.name || '',
      skill_name_context: skillName || '',
      target_role: targetRole || '',
      session_id: userSessionDetails?.session_id || '',
      rated_skills_count: skillLevels.filter(skill => skill.level !== null).length + 1, // +1 for the one we just rated
      timestamp: new Date().toISOString()
    })

    if (nextSkill?.id <= 5) {
      setExpandedCard(nextSkill.id)
    }
    setHoveredLevel({ skillId: null, level: null })

  }

  const handleContinue = async () => {
    const ratedSkills = skillLevels.filter(skill => skill.level !== null)

    if (ratedSkills.length < 5) {
      toast.error('Please rate all 5 skills to continue')
      return
    }

    try {
      // Prepare subskill scores in the required format
      const subskill_scores = {}
      ratedSkills.forEach(skill => {
        subskill_scores[skill.name || skill.title] = skill.level
      })

      console.log('Prepared subskill scores:', subskill_scores)

      // Track continue button click with complete skill ratings
      Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_CONTINUE_CLICKED, {
        rated_skills_count: ratedSkills.length,
        skill_ratings: subskill_scores,
        skill_name_context: skillName || '',
        target_role: targetRole || '',
        user_name: firstName,
        session_id: userSessionDetails?.session_id || '',
        flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
        experience_level: experienceLevel,
        timestamp: new Date().toISOString()
      })

      // Save subskill scores to backend
      await dispatch(saveSubskillScores({
        session_id: userSessionDetails?.session_id,
        subskill_scores: subskill_scores
      })).unwrap()

      console.log('Starting questions polling...')
      setRateSelected(true)

    } catch (error) {
      console.error('Error saving subskill scores:', error)
      toast.error('Failed to save skill ratings. Please try again.')
    }
  }



  const getRatedCount = () => skillLevels.filter(skill => skill.level !== null).length

  const getLevelInfo = (levelValue) => {
    return levelOptions.find(option => option.value === levelValue)
  }


  const handleTakeSkillCheck = () => {
    // Track skill check button click
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP3_SKILL_CHECK_CLICKED, {
      skill_name_context: skillName || '',
      target_role: targetRole || '',
      user_name: firstName,
      session_id: userSessionDetails?.session_id || '',
      flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
      experience_level: experienceLevel,
      next_step: '/onboarding/loader-2',
      timestamp: new Date().toISOString()
    })

    navigate('/onboarding/loader-2')
  }

  if (isBeginner) {
    return (
      <div className='mt-8 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col justify-between items-center'>
        <div className='w-full flex flex-col justify-start items-center'>
          <div className='w-full flex flex-col justify-start items-center'>
            <ProgressBar currentStep={3} totalSteps={6} stepText={`Step 3/6 to ${targetRole ? 'becoming ' + targetRole : 'mastering ' + capitalise(skillName)}`} />
          </div>


          <div className='w-full mt-4 flex flex-col p-[2px] rounded-[1.5rem]' style={{
            backgroundImage: `linear-gradient(
          to right,
          rgba(64, 106, 255, 0.3) 0%,
          rgba(64, 106, 255, 1) 56%,
          rgba(64, 106, 255, 0.3) 100%
        )`
          }}>
            {/* Main content card */}
            <div className='flex flex-col p-6 rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
              <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
                style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />
              {/* Content */}
              <div className='flex flex-col gap-6 relative z-10'>

                {/* Header */}
                <div className='flex flex-col'>
                  <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                    What would help you right now?
                  </h1>
                </div>

                {/* Experience level options */}
                <div className='flex flex-col gap-3 w-full'>
                  {experienceOptions.map((option, index) => {
                    const isSelected = selectedLevel?.value === option.value

                    return (
                      <div
                        key={index}
                        className={`${isSelected
                          ? 'bg-[#7FC241]/20 border-[#7FC241] shadow-[0px_0px_2px_0px_#7fc241]'
                          : 'bg-[#406AFF]/15 border-[#406AFF]/70 hover:bg-[#406AFF]/25 hover:border-[#406AFF]'
                          } border border-solid flex gap-2 items-center p-4 rounded-lg cursor-pointer transition-all duration-200 w-full group`}
                        onClick={() => handleOptionClick(option)}
                      >
                        {/* Radio button */}
                        <div className='relative w-[14px] h-[14px] shrink-0'>
                          <div className={`w-[14px] h-[14px] rounded-full border-2 ${isSelected
                            ? 'border-[#7FC241]'
                            : 'border-[#70AEFF]'
                            }`}>
                            <div className={`w-[6px] h-[6px] rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all ${isSelected
                              ? 'bg-[#7FC241] opacity-100'
                              : 'bg-[#70AEFF] opacity-0'
                              }`}></div>
                          </div>
                        </div>

                        {/* Option content */}
                        <div className='flex flex-1 items-center justify-between text-[14px] text-white'>
                          <span className='capitalize'>{option.text}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>


        <div className='mt-6 flex flex-col gap-0.5 items-center justify-center'>
          <div className='flex items-center justify-center'>
            <div className='animate-pulse text-[#83BEFF]'>
              <img alt="Personalisation in progress" src="https://assets.learntube.ai/files/personalise/Vector%20(9).webp" className='w-8 h-8' />
            </div>
          </div>
          <p className='font-bold text-[#83BEFF] text-[0.75rem] tracking-[0.6px] uppercase'>
            Personalisation in progress…
          </p>

        </div>

      </div>
    )
  }


  return (
    <div className='mt-8 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col justify-between items-center'>
      {/* Progress bar and step indicator */}
      <div className='w-full flex flex-col justify-start items-center'>
        <ProgressBar
          currentStep={isRoleFlow ? 5 : 3}
          totalSteps={isRoleFlow ? 8 : 6}
          stepText={`Step ${isRoleFlow ? 5 : 3}/${isRoleFlow ? 8 : 6} to  ${targetRole ? 'becoming ' + targetRole : 'mastering ' + capitalise(skillName)}`}
        />

        {!rateSelected ? <div className='w-full mt-4 flex flex-col p-[2px] rounded-[1.5rem]' style={{
          backgroundImage: `linear-gradient(
            to right,
            rgba(64, 106, 255, 0.3) 0%,
            rgba(64, 106, 255, 1) 56%,
            rgba(64, 106, 255, 0.3) 100%
          )`
        }}>
          <div className='w-full flex flex-col p-[2px] rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>

            <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
              style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />

            {/* Main content */}
            <div className='w-full flex flex-col p-6 relative z-10 gap-6'>
              {/* Header */}
              <div className='w-full flex flex-col'>
                <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                  Rate your {capitalise(skillName)} skills
                </h1>
              </div>

              {/* Skill Cards */}
              <div className='w-full flex flex-col gap-2'>
                {skillLevels.map((skill) => (
                  <div
                    key={skill.id}
                    className={`${expandedCard === skill.id
                      ? 'border-[#7FC241] border-1 shadow-[0px_0px_2px_0px_#7fc241]'
                      : ''
                      } w-full bg-gradient-to-r from-[#07162f] via-[#072140] to-[#07162f] rounded-xl px-4 py-3 cursor-pointer transition-all duration-300`}
                    style={{
                      background: expandedCard === skill.id
                        ? 'linear-gradient(90deg, #07162f 0%, #072140 48%, #07162f 100%)'
                        : 'linear-gradient(90deg, #07162f 0%, #072140 48%, #07162f 100%)'
                    }}
                    onClick={() => handleCardClick(skill.id)}
                  >
                    {/* Collapsed state */}
                    {expandedCard !== skill.id && (
                      <div className='w-full flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <span className='text-white font-medium text-[0.875rem]'>
                            {skill.name}
                          </span>
                          {skill.level && (
                            <div
                              className='px-2 py-1 rounded-lg text-[0.625rem] font-normal'
                              style={{
                                backgroundColor: getLevelInfo(skill.level)?.bgColor,
                                color: getLevelInfo(skill.level)?.color
                              }}
                            >
                              {getLevelInfo(skill.level)?.label}
                            </div>
                          )}
                        </div>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className='text-white/60 text-sm transform rotate-180'
                        />
                      </div>
                    )}

                    {/* Expanded state */}
                    {expandedCard === skill.id && (
                      <div className='flex flex-col gap-4'>
                        {/* Header with close button */}
                        <div className='flex items-center justify-between'>
                          <span className='text-white font-medium text-[0.875rem]'>
                            {skill.name}
                          </span>
                          <FontAwesomeIcon
                            icon={faChevronUp}
                            className='text-white/60 text-sm'
                          />
                        </div>

                        {/* Level selection */}
                        <div className='flex flex-col gap-1'>
                          {/* Interactive progress bar with slider */}
                          <div className='relative flex items-center w-full'>
                            {/* Progress bar track */}
                            <div
                              className='h-1 bg-[#406AFF]/30 rounded-full cursor-pointer w-full'
                              onClick={(e) => {
                                e.stopPropagation()
                                const rect = e.currentTarget.getBoundingClientRect()
                                const clickX = e.clientX - rect.left
                                const percentage = (clickX / rect.width) * 100

                                let selectedLevel = 'new'
                                if (percentage > 75) selectedLevel = 'expert'      // 75-100%
                                else if (percentage > 50) selectedLevel = 'skilled'  // 50-75%
                                else if (percentage > 25) selectedLevel = 'basic'    // 25-50%
                                else selectedLevel = 'new'                          // 0-25%

                                handleLevelSelect(skill.id, selectedLevel)
                              }}
                              onMouseLeave={() => setHoveredLevel({ skillId: null, level: null })}
                            >
                              {/* Progress fill - aligned with labels */}
                              <div
                                className='h-full bg-[#406AFF] rounded-full transition-all duration-300 relative'
                                style={{
                                  width: skill.level
                                    ? `${(levelOptions.findIndex(opt => opt.value === skill.level) + 1) * 25 - 12.5}%`
                                    : '0%'
                                }}
                              />

                              {/* Starting point indicator (blue circle at "New" when no selection) */}
                              {!skill.level && (
                                <div
                                  className='absolute w-3 h-3 md:w-4 md:h-4 bg-[#406AFF] rounded-full border-2 border-[#012744] shadow-lg transition-all duration-200 z-20'
                                  style={{
                                    left: '12.5%', // Centered on first 25% segment
                                    top: '50%',
                                    transform: 'translateX(-50%) translateY(-50%)',
                                    boxShadow: '0px 0px 6px 0px #406AFF'
                                  }}
                                />
                              )}

                              {/* Circular slider indicator - green when level is selected */}
                              {skill.level && (
                                <div
                                  className='absolute w-3 h-3 md:w-4 md:h-4 bg-[#406AFF] rounded-full border-2 border-[#012744] shadow-lg cursor-grab active:cursor-grabbing hover:scale-110 transition-all duration-200 z-20'
                                  style={{
                                    left: `${(levelOptions.findIndex(opt => opt.value === skill.level) + 1) * 25 - 12.5}%`, // Centered in each 25% segment
                                    top: '50%',
                                    transform: 'translateX(-50%) translateY(-50%)',
                                    boxShadow: '0px 0px 6px 0px #406AFF'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}

                              {/* Hover preview indicator */}
                              {hoveredLevel.skillId === skill.id && hoveredLevel.level && hoveredLevel.level !== skill.level && (
                                <div
                                  className='absolute w-2.5 h-2.5 md:w-4 md:h-4 bg-[#7FC241]/60 rounded-full border-2 md:border-[3px] border-[#7FC241]/80 transition-all duration-200 z-10'
                                  style={{
                                    left: `${(levelOptions.findIndex(opt => opt.value === hoveredLevel.level) + 1) * 25 - 12.5}%`,
                                    top: '50%',
                                    transform: 'translateX(-50%) translateY(-50%)'
                                  }}
                                />
                              )}
                            </div>

                            {/* Level click zones - aligned with labels */}
                            {levelOptions.map((option, index) => (
                              <div
                                key={`zone-${option.value}`}
                                className='absolute top-0 h-full cursor-pointer z-10'
                                style={{
                                  left: `${index * 25}%`,
                                  width: '25%'
                                }}
                                onMouseEnter={() => setHoveredLevel({ skillId: skill.id, level: option.value })}
                                onMouseLeave={() => setHoveredLevel({ skillId: null, level: null })}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleLevelSelect(skill.id, option.value)
                                }}
                              />
                            ))}
                          </div>

                          {/* Level labels - exactly aligned with zones */}
                          <div className='flex items-center justify-between text-[0.625rem] mt-2 w-full'>
                            {levelOptions.map((option, index) => (
                              <div
                                key={`label-${option.value}`}
                                className={`cursor-pointer transition-colors text-center ${skill.level === option.value || (!skill.level && option.value === 'new')
                                  ? 'text-white font-medium'
                                  : 'text-white/30'
                                  }`}
                                style={{ width: '25%' }} // Each label takes exactly 25% width
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleLevelSelect(skill.id, option.value)
                                }}
                              >
                                {option.label}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div className='flex flex-col gap-3'>
                <button
                  onClick={handleContinue}
                  disabled={getRatedCount() < 5 || subskillsSaving}
                  className={`${getRatedCount() < 5 || subskillsSaving
                    ? 'bg-[#7FC241]/50 cursor-not-allowed '
                    : 'bg-[#7FC241] hover:bg-[#8bd845] border-b-2 border-l-2 border-r-2'
                    }  border-[#8bd845] h-12 rounded-lg font-semibold text-[#121212] text-lg transition-colors flex items-center justify-center gap-2`}
                >
                  {subskillsSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-[#121212] border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    'Rate All 5 To Continue'
                  )}
                </button>

              </div>
            </div>
          </div>
        </div> : <div className='w-full mt-4 flex flex-col p-[2px] rounded-[1.5rem]' style={{
          backgroundImage: `linear-gradient(
            to right,
            rgba(64, 106, 255, 0.3) 0%,
            rgba(64, 106, 255, 1) 56%,
            rgba(64, 106, 255, 0.3) 100%
          )`
        }}>
          <div className='w-full flex flex-col rounded-[1.5rem]  bg-[#0c2346] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] relative overflow-hidden p-6 items-center justify-center '>
            <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
              style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />
            <div className='w-full flex flex-col'>
              <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                Let’s confirm your skill level to personalise your course
              </h1>
            </div>
            <div className='w-full flex flex-col mt-8 gap-4'>
              <div className='w-full flex-1 flex gap-2  justify-center items-center'>
                <div className='w-full flex-1 bg-white/15 h-[1px] w-fit' />
                <div className='font-semibold text-[12px] text-white/50'>WHAT YOU GET</div>
                <div className='w-full flex-1 bg-white/15 h-[1px] w-fit' />

              </div>
              <div className='flex flex-col gap-2'>
                {/* First completion item */}
                <div className='flex items-center gap-3 p-4 bg-[#406AFF]/10 rounded-xl'>
                  <div className='flex items-center justify-center flex-shrink-0'>
                    <FontAwesomeIcon icon={faCheck} className='text-[#54B226] text-sm' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-white font-normal text-[12px] leading-tight'>
                      See your current skill score and understand exactly where you stand
                    </p>
                  </div>
                </div>

                {/* Second completion item */}
                <div className='flex items-center gap-3 p-4 bg-[#406AFF]/10 rounded-xl'>
                  <div className='flex items-center justify-center flex-shrink-0'>
                    <FontAwesomeIcon icon={faCheck} className='text-[#54B226] text-sm' />
                  </div>
                  <div className='flex-1'>
                    <p className='text-white font-normal text-[12px] leading-tight'>
                      Discover strengths & gaps and know what to focus on, not just what to learn
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className={`w-full px-[1px] mt-8 pb-[1px] rounded-[8px] z-[999999999] cursor-pointer`} onClick={handleTakeSkillCheck} style={{
              background: `linear-gradient(
    to right,
    #8BD845 0%,
    #C5E2AB 100%
  )`}}>
              <button

                disabled={false}
                className={`w-full bg-[#7FC241] hover:bg-[#8bd845]
                    h-12 rounded-lg font-bold text-[#121212] text-lg transition-colors`}
              >
                Take Quick Skill Check <FontAwesomeIcon icon={faArrowRight} className='ml-1 text-[#121212] arrow-move text-sm' />
              </button>

            </div>
            <img src="https://assets.learntube.ai/files/personalise/Frame%20427322728%20(1).webp" alt="" className='h-auto w-[188px] mt-4' />
          </div>
        </div>
        }
      </div>

      {/* Personalisation indicator */}
      <div className='mt-6 flex flex-col gap-1 items-center justify-center'>
        <p className='font-bold text-[#83BEFF] text-[0.75rem] tracking-[0.6px] uppercase'>
          Personalisation in progress…
        </p>
        <div className='flex items-center justify-center'>
          <div className='animate-pulse text-[#83BEFF]'>
            <img alt="Personalisation in progress" src="https://assets.learntube.ai/files/personalise/Vector%20(9).webp" className='w-8 h-8' />
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingStep3